import { Autocomplete, AutocompleteChangeDetails, Grid, Modal, TextField } from "@mui/material";
import { Box } from "@mui/system";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import "./UserAnalytics.scss";
import { setOpenModal, setSelectedDay, setSelectedUserAnalyticsByDays, setUserActivityDetails, UserAnalyticsSelector } from "./reducers/UserAnalyticsReducer";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Chart,
} from 'chart.js';
import { Bar, getDatasetAtEvent, getElementAtEvent } from 'react-chartjs-2';
import { MouseEvent, useEffect, useRef, useState } from "react";
import UserAnalyticsApi, { GetUserActivityDetailsParam, GetUserAnalyticsByDaysParam, UserActivityType } from "./api/UserAnalyticsApi";
import BaseResponse from "../../app/BaseResponse";
import { UserAnalyticsByDaysModel } from "./models/UserAnalyticsByDaysModel";
import Utility from "../../app/utility";
import { showLoading } from "../LoadingView/reducers/LoadingViewReducer";
import moment from 'moment'
import { UserActivityHistoryModel } from "./models/UserActivityHistoryModel";
import DatatableColumnModel from "../Datatable/DatatableColumnModel";
import Datatable, { PagingTypeEnum } from "../Datatable/Datatable";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const options = {
    responsive: true,
    plugins: {
        legend: {
            position: 'top' as const,
        },
        title: {
            display: true,
        },
    },
    aspectRatio: 3
};

const UserAnalytics: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const userAnalyticsData = useSelector(UserAnalyticsSelector);
    const [chartData, setChartData] = useState<any>();
    const chartRef = useRef<ChartJS<"bar">>();

    useEffect(() => {
        if (userAnalyticsData.selectedUserId > 0) {
            dispatch(showLoading(true));
            UserAnalyticsApi.GetUserAnalyticsByDays({
                day: userAnalyticsData.selectedDay,
                userId: userAnalyticsData.selectedUserId
            } as GetUserAnalyticsByDaysParam)
                .then((response: BaseResponse<UserAnalyticsByDaysModel[]>) => {
                    if (response.success) {
                        dispatch(setSelectedUserAnalyticsByDays(response.data));
                        mapChartData(response.data);
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                }).catch((error) => {
                    Utility.Alert.Error(error);
                })
                .finally(() => {
                    dispatch(showLoading(false));
                });
        }
    }, [userAnalyticsData.selectedUserId, userAnalyticsData.selectedDay]);

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: "80%",
        height: "80vh",
        bgcolor: 'white',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        overflow: "auto"
    };

    const mapChartData = (data: UserAnalyticsByDaysModel[]) => {
        let labels = [];
        let today = new Date();
        for (let index = userAnalyticsData.selectedDay - 1; index >= 0; index--) {
            let day = Utility.addDaysToDate(today, -index);

            labels.push(Utility.DateFormat.getDateDDMMYYYY(day.getTime()));
        }

        const chartDataConfig = {
            labels,
            datasets: [
                {
                    label: 'Search',
                    data: labels.map((label) => {
                        let selectedDate = moment(label, 'DD/MM/YYYY').toDate();
                        let selectedChartData = _.find(data, (item) => {
                            return new Date(item.year, item.month - 1, item.day).getTime() === selectedDate.getTime();
                        });

                        return selectedChartData ? selectedChartData.search : 0;
                    }),
                    backgroundColor: 'rgba(255, 99, 132, 0.5)',
                },
                {
                    label: 'Input',
                    data: labels.map((label) => {
                        let selectedDate = moment(label, 'DD/MM/YYYY').toDate();
                        let selectedChartData = _.find(data, (item) => {
                            return new Date(item.year, item.month - 1, item.day).getTime() === selectedDate.getTime();
                        });

                        return selectedChartData ? selectedChartData.input : 0;
                    }),
                    backgroundColor: 'rgba(53, 162, 235, 0.5)',
                },
            ],
        };

        setChartData(chartDataConfig);
    };

    const GetUserActivityDetail = (selectedAnalytics: UserAnalyticsByDaysModel, userActivityType: UserActivityType) => {
        let param = {
            year: selectedAnalytics.year,
            month: selectedAnalytics.month,
            day: selectedAnalytics.day,
            userId: userAnalyticsData.selectedUserId,
            userActivityType: userActivityType.toString()
        } as GetUserActivityDetailsParam;

        dispatch(showLoading(true));

        UserAnalyticsApi.GetUserActivityDetails(param)
            .then((response: BaseResponse<UserActivityHistoryModel[]>) => {
                if (response.success) {
                    dispatch(setUserActivityDetails(response.data));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            });
    };

    const columns = [
        {
            Field: "activityType",
            Title: "Activity Type",
            AllowSort: true,
            Render: (rowData: UserActivityHistoryModel) => {
                let inputActivity = rowData.activityType == "Input" ?
                    rowData.details === "null" ? " - Add New" : " - Update" : "";
                return <>
                    {rowData.activityType + inputActivity}
                </>
            }
        } as DatatableColumnModel<UserActivityHistoryModel>,
        {
            Field: "createdDate",
            Title: "Created Date",
            AllowSort: true,
        } as DatatableColumnModel<UserActivityHistoryModel>,
        {
            Field: "activityDetailType",
            Title: "Target Type",
            AllowSort: true,
        } as DatatableColumnModel<UserActivityHistoryModel>,
        {
            Field: "details",
            Title: "Details",
            AllowSort: true,
            Render: (rowData: UserActivityHistoryModel) => {
                let details = "";
                if (rowData.details !== "null") {
                    let rowDetails = JSON.parse(rowData.details);
                    for (let item in rowDetails) {
                        details += item + ":" + rowDetails[item] + "<br/>"
                    }
                }

                return <div dangerouslySetInnerHTML={{ __html: details }} />
            }
        } as DatatableColumnModel<UserActivityHistoryModel>,
    ];

    return (
        <>
            <Modal
                open={userAnalyticsData.openModal}
                onClose={() => {
                    dispatch(setOpenModal(false));
                }}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style} className="nice-scroll">
                    <Grid
                        container
                        justifyContent="flex-end"
                        className="table-search-result-utility-bar"
                    >
                        <Grid>
                            <Autocomplete
                                className='cargo-input-component'
                                options={[3, 7, 14, 30, 60]}
                                getOptionLabel={(option: number) => {
                                    return option.toString();
                                }}
                                disableClearable={true}
                                renderInput={(params) => <TextField {...params} label="Display days" />}
                                value={userAnalyticsData.selectedDay}
                                onChange={(event: React.SyntheticEvent, value: number | null, reason: string, details?: AutocompleteChangeDetails<number> | undefined) => {
                                    dispatch(setSelectedDay(value ? value : 3));
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid className="user-analytics-chart">
                        {
                            chartData != null &&
                            <Bar
                                options={options}
                                data={chartData}
                                ref={chartRef}
                                onClick={(event: MouseEvent<HTMLCanvasElement>) => {
                                    const { current: chart } = chartRef;

                                    if (!chart) {
                                        return;
                                    }

                                    let selectedElement = getElementAtEvent(chart, event);
                                    if (selectedElement.length == 0) {
                                        return;
                                    }

                                    let selectedIndex = selectedElement[0].index;
                                    let userActivityType = selectedElement[0].datasetIndex === 0 ? UserActivityType.Search : UserActivityType.Input;

                                    let selectedDateLabel = chartData.labels[selectedIndex];
                                    let selectedDate = moment(selectedDateLabel, 'DD/MM/YYYY').toDate()

                                    let selectedChartItem = _.find(userAnalyticsData.selectedUserAnalyticsByDays, (item) => {
                                        return item.year === selectedDate.getFullYear() &&
                                            item.month === selectedDate.getMonth() + 1 &&
                                            item.day === selectedDate.getDate();
                                    });

                                    if (selectedChartItem) {
                                        GetUserActivityDetail(selectedChartItem, userActivityType);
                                    }
                                }}
                            />
                        }
                    </Grid>
                    <Grid className="user-analytics-data-detail">
                        <Datatable<UserActivityHistoryModel>
                            useCheckBox={false}
                            useDefaultSearchBar={false}
                            pagingType={PagingTypeEnum.None}
                            data={userAnalyticsData.userActivityDetails}
                            useItemPerPage={false}
                            columns={columns}
                            isDataLoaded={true}
                            useHeaderSort={false}
                            stickyHeader={true}
                        ></Datatable>
                    </Grid>
                </Box>
            </Modal>
        </>
    )
};

export default UserAnalytics;